import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CLONE_ITEM_COUNT_MAX, CLONE_ITEM_COUNT_MIN } from '@nohara-dxdev/mt-common';
import { useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCloneItem } from '../api';
import { getItemsQueryKey } from '../api/get-items';
import { useJobStatus } from '../api/get-job-status';
import { updateItem } from '../api/update-item';

import { CloneEditRow, CloneItemEditStep, isCloneEditRowChanged } from './clone-item-edit-step';

interface CloneItemDialogProps {
  itemId: string;
  itemName: string;
}

// MT1-1428: after the clone job completes, the dialog moves to an edit step
// where the user can adjust each clone's number/name before returning to the
// grid. The clone flow is: count → cloning (job polling) → edit.
type CloneStep = 'count' | 'cloning' | 'edit';

export const CloneItemDialog = ({ itemId, itemName }: CloneItemDialogProps) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<CloneStep>('count');
  const [countInput, setCountInput] = useState('1');
  const [countError, setCountError] = useState<string | null>(null);
  const [editRows, setEditRows] = useState<CloneEditRow[]>([]);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const { orgId, projectId } = useScopeIds('org', 'project');

  // MT1-1426: clone is async. The mutation enqueues a job; we stash the jobId
  // and poll GET /jobs/{jobId} until it reaches a terminal status. The clone
  // icon shows an in-flight state while the worker runs.
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const jobStatusQuery = useJobStatus({ jobId: activeJobId });

  // A polling error is terminal too — otherwise a failed status request would
  // leave `inFlight` stuck true and the clone control disabled forever.
  const inFlight =
    activeJobId !== null &&
    !jobStatusQuery.isError &&
    jobStatusQuery.data?.status !== 'completed' &&
    jobStatusQuery.data?.status !== 'failed';

  const cloneItem = useCloneItem({
    mutationConfig: {
      onSuccess: (job) => {
        // Hand off to the polling effect. The dialog stays open on a progress
        // step so it can transition to the edit screen when the worker
        // finishes; closing it mid-clone falls back to the toast-only flow.
        setActiveJobId(job.id);
        setStep('cloning');
      },
      onError: () => {
        notifications.error(t('items.clone.error', { defaultValue: 'Failed to clone item' }));
      },
    },
  });

  useEffect(() => {
    if (!activeJobId) return;
    // Treat a failed status request as terminal so the clone control unlocks.
    if (jobStatusQuery.isError) {
      notifications.error(t('items.clone.error', { defaultValue: 'Failed to clone item' }));
      setActiveJobId(null);
      if (open && step === 'cloning') setOpen(false);
      return;
    }
    const job = jobStatusQuery.data;
    if (!job) return;
    if (job.status === 'completed') {
      // Items + zones invalidate via the DDB stream when the worker writes the
      // clones; trigger again here as a backstop so the grid refreshes when the
      // user sees the result.
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });
      void queryClient.invalidateQueries({ queryKey: ['zones'] });
      const clonedItems = job.response?.items;
      if (open && step === 'cloning' && clonedItems && clonedItems.length > 0) {
        // Dialog is still up: move to the edit step instead of toasting, so
        // the user can adjust each clone's number/name right away (MT1-1428).
        setEditRows(
          clonedItems.map((item) => ({
            id: item.id,
            itemNumber: item.itemNumber,
            name: item.name,
            initialItemNumber: item.itemNumber,
            initialName: item.name,
            status: 'idle' as const,
          })),
        );
        setStep('edit');
      } else {
        // Dialog already closed (or no per-clone data on the response):
        // fall back to the toast-only completion flow.
        notifications.success(
          t('items.clone.success', {
            count: job.response?.count ?? 0,
            defaultValue: 'Items cloned successfully',
          }),
        );
        if (open && step === 'cloning') setOpen(false);
      }
      setActiveJobId(null);
    } else if (job.status === 'failed') {
      notifications.error(t('items.clone.error', { defaultValue: 'Failed to clone item' }));
      setActiveJobId(null);
      if (open && step === 'cloning') setOpen(false);
    }
  }, [
    activeJobId,
    jobStatusQuery.data,
    jobStatusQuery.isError,
    queryClient,
    orgId,
    projectId,
    notifications,
    t,
    open,
    step,
  ]);

  const handleOpen = () => {
    setCountInput('1');
    setCountError(null);
    setEditRows([]);
    setStep('count');
    setOpen(true);
  };

  const handleClose = () => {
    // Closing during 'cloning' lets the job finish in the background (the
    // polling effect toasts on completion). Closing during 'edit' keeps the
    // worker-generated numbers/names — same as Skip.
    if (saving) return;
    setOpen(false);
  };

  const parsedCount = parseInt(countInput, 10);
  const isCountValid =
    !isNaN(parsedCount) &&
    parsedCount >= CLONE_ITEM_COUNT_MIN &&
    parsedCount <= CLONE_ITEM_COUNT_MAX;

  const handleConfirm = () => {
    cloneItem.mutate({ itemId, dto: { count: parsedCount } });
  };

  const handleCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setCountInput(raw);
    const value = parseInt(raw, 10);
    if (raw === '' || isNaN(value)) {
      setCountError(t('items.clone.countError.invalid'));
    } else if (value < CLONE_ITEM_COUNT_MIN || value > CLONE_ITEM_COUNT_MAX) {
      setCountError(
        t('items.clone.countError.range', {
          min: CLONE_ITEM_COUNT_MIN,
          max: CLONE_ITEM_COUNT_MAX,
        }),
      );
    } else {
      setCountError(null);
    }
  };

  const handleRowChange = (
    id: string,
    patch: Partial<Pick<CloneEditRow, 'itemNumber' | 'name'>>,
  ) => {
    setEditRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const hasInvalidRow = editRows.some(
    (row) => row.itemNumber.trim() === '' || row.name.trim() === '',
  );
  const hasPendingChanges = editRows.some(
    (row) => isCloneEditRowChanged(row) || row.status === 'error',
  );

  const handleSaveEdits = async () => {
    // PATCH only the rows the user changed (plus failed rows being retried),
    // in batches of 5 — same cadence as the bulk-edit dialog.
    const targets = editRows.filter((row) => isCloneEditRowChanged(row) || row.status === 'error');
    if (targets.length === 0) return;

    setSaving(true);
    // One state write per batch (not per request): the awaits below prevent
    // React from batching per-row updates itself, and at 100 clones that's a
    // lot of avoidable re-renders of the edit table.
    const applyPatches = (patches: Map<string, Partial<CloneEditRow>>) => {
      setEditRows((prev) => {
        return prev.map((row) => {
          const patch = patches.get(row.id);
          return patch ? { ...row, ...patch } : row;
        });
      });
    };

    let failed = false;
    for (let i = 0; i < targets.length; i += 5) {
      const batch = targets.slice(i, i + 5);
      applyPatches(new Map(batch.map((row) => [row.id, { status: 'saving', error: undefined }])));
      const outcomes = await Promise.all(
        batch.map(async (row): Promise<[string, Partial<CloneEditRow>]> => {
          // Trim before PATCH so the persisted values match what the
          // empty-field validation checked; write the trimmed values back so
          // the row no longer reads as changed after a successful save.
          const itemNumber = row.itemNumber.trim();
          const name = row.name.trim();
          try {
            await updateItem(orgId, projectId, row.id, { itemNumber, name });
            return [
              row.id,
              {
                status: 'saved',
                itemNumber,
                name,
                initialItemNumber: itemNumber,
                initialName: name,
              },
            ];
          } catch (err) {
            return [
              row.id,
              { status: 'error', error: err instanceof Error ? err.message : String(err) },
            ];
          }
        }),
      );
      applyPatches(new Map(outcomes));
      if (outcomes.some(([, patch]) => patch.status === 'error')) failed = true;
    }
    setSaving(false);

    void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });

    if (failed) {
      notifications.error(
        t('items.clone.edit.error', { defaultValue: 'Failed to update some cloned items' }),
      );
      return;
    }
    notifications.success(t('items.clone.edit.success', { defaultValue: 'Cloned items updated' }));
    setOpen(false);
  };

  const busy = cloneItem.isPending || inFlight;

  return (
    <>
      <Tooltip title={t('items.clone.tooltip', 'Clone item')}>
        {/* span keeps the tooltip working while the button is disabled */}
        <span>
          <IconButton size="small" onClick={handleOpen} disabled={busy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth={step === 'edit' ? 'md' : 'xs'} fullWidth>
        <DialogTitle>
          {step === 'edit'
            ? t('items.clone.edit.title', 'Edit Cloned Items')
            : t('items.clone.title', 'Clone Item')}
        </DialogTitle>
        <DialogContent>
          {step === 'count' && (
            <Stack spacing={2} mt={1}>
              <Typography variant="body2">
                {t('items.clone.description', {
                  name: itemName,
                  defaultValue: 'How many copies of "{{name}}" would you like to create?',
                })}
              </Typography>
              <TextField
                label={t('items.clone.countLabel', 'Number of copies')}
                type="number"
                value={countInput}
                onChange={handleCountChange}
                error={!!countError}
                helperText={countError}
                size="small"
                fullWidth
              />
            </Stack>
          )}
          {step === 'cloning' && (
            <Stack spacing={2} mt={1}>
              <Typography variant="body2">
                {t('items.clone.cloning', {
                  count: isCountValid ? parsedCount : 1,
                  defaultValue: 'Cloning {{count}} item(s)…',
                })}
              </Typography>
              <LinearProgress />
            </Stack>
          )}
          {step === 'edit' && (
            <CloneItemEditStep rows={editRows} saving={saving} onRowChange={handleRowChange} />
          )}
        </DialogContent>
        <DialogActions>
          {step === 'count' && (
            <>
              <Button onClick={handleClose} disabled={cloneItem.isPending}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={cloneItem.isPending || !isCountValid}
              >
                {t('items.clone.confirm', 'Clone')}
              </Button>
            </>
          )}
          {step === 'cloning' && (
            <Button onClick={handleClose}>{t('common.close', 'Close')}</Button>
          )}
          {step === 'edit' && (
            <>
              <Button data-testid="clone-edit-skip" onClick={handleClose} disabled={saving}>
                {t('items.clone.edit.skip', 'Skip')}
              </Button>
              <Button
                data-testid="clone-edit-save"
                variant="contained"
                onClick={() => void handleSaveEdits()}
                disabled={saving || hasInvalidRow || !hasPendingChanges}
              >
                {t('items.clone.edit.save', 'Save')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
